import { AD_CHANNEL_CODE, DEFAULT_GOOGLE_ADCONFIG, GoogleAFG, KuleAd, NONE_TEST_MODE_AD_CHANNEL_CODES } from '@kulee/ad'
import type { ShowInterstitialAdOptions, ShowPrerollAdOptions, ShowRewardAdOptions } from '@kulee/ad/types/types'
import type { GameAdConfig } from '@kulee/ad/types/types/GameAdConfig'
import { GAME_EVENTS } from '@kulee/tga-constant'
import BaseModule from './BaseModule'
import { RESPONSE_CODES } from './constant'
import logger from './logger'
import type { CallbackData } from './types'

class Ad extends BaseModule {
  private gameAdConfig!: GameAdConfig
  private kuleAdInstance!: KuleAd
  private status: 'unready' | 'ready' | 'error' = 'unready'
  private prerollAdShowing = false

  /**
   * 初始化游戏广告
   */
  public async initAd(): Promise<boolean> {
    logger.log('ad module start to init')

    this.gameAdConfig = await this.getGameAdConfig()
    const {
      testEnabled,
      channel: { channelCode }
    } = this.gameAdConfig

    // 测试广告渠道
    if (testEnabled && NONE_TEST_MODE_AD_CHANNEL_CODES.indexOf(channelCode) >= 0) {
      this.gameAdConfig = DEFAULT_GOOGLE_ADCONFIG
    }

    // Goolge AFG在游戏iframe中展示
    if (channelCode === AD_CHANNEL_CODE.GOOGLE_AFG) {
      this.kuleAdInstance = new KuleAd({
        channel: new GoogleAFG(),
        gameAdConfig: this.gameAdConfig
      })

      const inited = await this.kuleAdInstance.init()
      this.status = inited ? 'ready' : 'error'

      if (inited && this.prerollAdShowing) {
        this.showPrerollAd()
      }

      return inited
    }

    return new Promise(resolve => {
      this.pm.removeAll(GAME_EVENTS.ON_AD_INITED)
      this.pm.on(GAME_EVENTS.ON_AD_INITED, (data: boolean) => {
        this.status = data ? 'ready' : 'error'

        if (data && this.prerollAdShowing) {
          this.showPrerollAd()
        }
        resolve(data)
      })

      this.pm.emit(GAME_EVENTS.AD_INIT)
    })
  }

  /**
   * 展示开屏广告
   * @param options 必要参数
   */
  public showPrerollAd(options: ShowPrerollAdOptions = {}) {
    const opts = this.optionsEnhance(options, 'preroll')
    const { onError = () => {}, onFinish = () => {} } = opts

    // 广告未初始化
    if (this.status === 'unready') {
      this.prerollAdShowing = true
      return
    }

    // 广告初始化错误
    if (this.status === 'error') {
      onError({
        code: RESPONSE_CODES.AD_INIT_ERROR,
        message: RESPONSE_CODES.AD_INIT_ERROR
      })
      onFinish()
      return
    }

    if (this.kuleAdInstance) {
      this.kuleAdInstance.showPrerollAd(opts)
    } else {
      this.pm.removeAll(GAME_EVENTS.ON_AD_EVENT)
      this.pm.on(GAME_EVENTS.ON_AD_EVENT, ({ data }: any) => {
        this.handleAdEvent(data, opts)
      })

      this.pm.emit(GAME_EVENTS.SHOW_PREROLL_AD)
    }
  }

  /**
   * 展示插屏广告
   * @param options 必要参数
   */
  public showInterstitialAd(options: ShowInterstitialAdOptions) {
    const opts = this.optionsEnhance(options, 'interstitial')
    const { type, onError = () => {}, onFinish = () => {} } = opts

    // 广告初始化错误
    if (this.status === 'error') {
      onError({
        code: RESPONSE_CODES.AD_INIT_ERROR,
        message: RESPONSE_CODES.AD_INIT_ERROR
      })
      onFinish()
      return
    }

    if (this.kuleAdInstance) {
      this.kuleAdInstance.showInterstitialAd(opts)
    } else {
      // on play event
      this.pm.removeAll(GAME_EVENTS.ON_AD_EVENT)
      this.pm.on(GAME_EVENTS.ON_AD_EVENT, ({ data }: any) => {
        this.handleAdEvent(data, opts)
      })

      this.pm.emit(GAME_EVENTS.SHOW_INTERSTITIAL_AD, {
        type
      })
    }
  }

  /**
   * 播放激励广告
   * @param options 必要参数
   */
  public showRewardAd(options: ShowRewardAdOptions) {
    const opts = this.optionsEnhance(options, 'reward')
    const { onError = () => {}, onFinish = () => {} } = opts

    // 广告初始化错误
    if (this.status === 'error') {
      onError({
        code: RESPONSE_CODES.AD_INIT_ERROR,
        message: RESPONSE_CODES.AD_INIT_ERROR
      })
      onFinish()
      return
    }

    if (this.kuleAdInstance) {
      this.kuleAdInstance.showRewardAd(opts)
    } else {
      // on play event
      this.pm.removeAll(GAME_EVENTS.ON_AD_EVENT)
      this.pm.on(GAME_EVENTS.ON_AD_EVENT, ({ data }: any) => {
        this.handleAdEvent(data, opts)
      })

      this.pm.emit(GAME_EVENTS.PRE_SHOW_REWARD_AD)
    }
  }

  /**
   * 展示插屏广告（兼容旧版本）
   */
  public showSlotAd(options: Omit<ShowInterstitialAdOptions, 'name'>): void {
    return this.showInterstitialAd(options)
  }

  /**
   * 展示开屏广告（兼容旧版本）
   * 现在版本开屏广告直接系统调用，不需要手动调用
   */
  public showTitleAd(options: any): void {
    if (options?.onFinish) {
      options.onFinish()
    }
  }

  public showBannerAd(options: any): void {
    logger.log('show banner ad: ', options)
  }

  public hideBannerAd(): void {
    logger.log('hide banner ad')
  }

  private optionsEnhance<T extends ShowPrerollAdOptions | ShowInterstitialAdOptions | ShowRewardAdOptions>(
    options: T,
    type: 'preroll' | 'interstitial' | 'reward'
  ): T {
    const { onPlay, onError, onFinish, beforePlay, onReward } = options as ShowInterstitialAdOptions &
      ShowRewardAdOptions

    const adName = this.getAdName()
    return {
      ...options,
      name: adName,
      onRequest: () => {
        this.pm.emit(GAME_EVENTS.SEND_AD_EVENT, {
          action: 'onRequest',
          type
        })
      },
      onPlay: () => {
        onPlay && onPlay()
        this.pm.emit(GAME_EVENTS.SEND_AD_EVENT, {
          name: adName,
          action: 'onPlay',
          type
        })
      },
      onError: params => {
        onError && onError(params)
        this.pm.emit(GAME_EVENTS.SEND_AD_EVENT, {
          name: adName,
          action: 'onError',
          type,
          payload: params
        })
      },
      onFinish: () => {
        onFinish && onFinish()
        this.pm.emit(GAME_EVENTS.SEND_AD_EVENT, {
          name: adName,
          action: 'onFinish',
          type
        })
      },

      beforePlay:
        type === 'reward'
          ? opts => {
              beforePlay && beforePlay(opts)
              this.pm.emit(GAME_EVENTS.SEND_AD_EVENT, {
                name: adName,
                action: 'beforePlay',
                type
              })
            }
          : undefined,
      onReward: () => {
        onReward && onReward()
        this.pm.emit(GAME_EVENTS.SEND_AD_EVENT, {
          name: adName,
          action: 'onReward',
          type
        })
      }
    }
  }

  private handleAdEvent(data: any, opts: any) {
    const { action, payload } = data
    const {
      onRequest = () => {},
      beforePlay = () => {},
      onPlay = () => {},
      onReward = () => {},
      onError = () => {},
      onFinish = () => {}
    } = opts

    switch (action) {
      case 'onRequest':
        onRequest()
        break
      // 激励广告专用
      case 'beforePlay':
        beforePlay(() => {
          // emit play event
          this.pm.emit(GAME_EVENTS.SHOW_REWARD_AD)
        })
        break
      case 'onPlay':
        onPlay()
        break
      // 激励广告专用
      case 'onReward':
        onReward()
        break
      case 'onError':
        onError(payload)
        break
      case 'onFinish':
        onFinish()
        break
      default:
        break
    }
  }

  private getAdName() {
    return this.context.sdk.__game ? this.context.sdk.__game.name : 'UNKNOWN'
  }

  /**
   * 获取广告配置
   */
  private async getGameAdConfig(): Promise<GameAdConfig> {
    return new Promise((resolve, reject) => {
      this.pm.removeAll(GAME_EVENTS.ON_AD_CONFIG_RECEIVED)
      this.pm.on(GAME_EVENTS.ON_AD_CONFIG_RECEIVED, ({ data }: { data: CallbackData }) => {
        logger.info('receive ad config from tga', data)

        if (data.error) {
          reject(data.error)
        } else {
          resolve(data.payload)
        }
      })

      this.pm.emit(GAME_EVENTS.GET_AD_CONFIG)
    })
  }
}

export default Ad
